import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { SessionUser } from '@bidmanager/shared';
import * as api from './api';

interface AuthState {
  user: SessionUser | null;
  ready: boolean;
  signIn: (email: string, password: string, clientSlug?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    void api.restore().then((restored) => {
      // The role gate applies to restored sessions too, or a CLIENT session
      // left over in localStorage would open the console shell.
      if (restored && restored.role !== 'ADMIN') {
        api.clearSession();
        setUser(null);
      } else {
        setUser(restored);
      }
      setReady(true);
    });
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      ready,
      signIn: async (email, password, clientSlug) => {
        queryClient.clear();
        const signedIn = await api.login(email, password, clientSlug);
        // This console is ADMIN-only. The API enforces that on every request —
        // rejecting the session here just tells the person immediately instead
        // of letting them in to watch every page 403.
        if (signedIn.role !== 'ADMIN') {
          await api.logout();
          throw new Error('This console is for instance administrators only.');
        }
        setUser(signedIn);
      },
      signOut: async () => {
        await api.logout();
        queryClient.clear();
        setUser(null);
      },
    }),
    [user, ready, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
